import { useParams, Link, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle2, Target, Zap, TrendingUp, Server, Database, Shield, Cloud, Network } from "lucide-react";
import { getServiceById } from "./servicesData";
import { breadcrumbSchema, faqSchema, serviceSchema, usePageSeo } from "./seo";
const LOGO_URL = "/logo_suanuvem.avif";
function ServicePage() {
  const { serviceId } = useParams();
  const service = getServiceById(serviceId);
  const servicePath = `/servicos/${serviceId}`;

  usePageSeo({
    title: service
      ? `${service.title} | Sua Nuvem`
      : "Serviço não encontrado | Sua Nuvem",
    description: service
      ? service.description
      : "Conheça as soluções da Sua Nuvem para cloud privada, DevOps, FinOps, segurança e operação de infraestrutura.",
    path: servicePath,
    structuredData: service
      ? [
          serviceSchema(service, servicePath),
          service.faq ? faqSchema(service.faq) : null,
          breadcrumbSchema([
            { name: "Início", path: "/" },
            { name: "Serviços", path: "/#solucoes" },
            { name: service.title, path: servicePath },
          ]),
        ]
      : [],
  });

  // Scroll to top when component mounts or serviceId changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [serviceId]);

  if (!service) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-brand-dark text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-brand-dark/70 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Sua Nuvem" className="h-14 w-auto mb-4" width="140" height="56" />
          </Link>
          <nav className="hidden md:flex gap-8 text-sm">
            <Link to="/" className="text-gray-300 hover:text-brand-sage transition-colors">
              Início
            </Link>
            <Link to="/#solucoes" className="text-gray-300 hover:text-brand-sage transition-colors">
              Soluções
            </Link>
            <Link to="/cases" className="text-gray-300 hover:text-brand-sage transition-colors">
              Cases
            </Link>
            <Link to="/blog" className="text-gray-300 hover:text-brand-sage transition-colors">
              Blog
            </Link>
            <Link to="/#contato" className="text-gray-300 hover:text-brand-sage transition-colors">
              Contato
            </Link>
          </nav>
          <button
            onClick={(e) => {
              e.preventDefault();
              window.location.replace('/#contato');
            }}
            className="bg-brand-sage text-brand-dark px-6 py-2 rounded-md font-semibold hover:bg-opacity-90 transition-all cursor-pointer"
            data-testid="header-cta-button"
            aria-label="Fale Conosco"
          >
            Fale Conosco
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-16 px-6 md:px-12" data-testid="service-hero">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-brand-sage hover:underline mb-8"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar para o Início
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight leading-tight mb-4">
              {service.title}
            </h1>
            <p className="text-xl md:text-2xl text-brand-sage font-semibold mb-6">
              {service.subtitle}
            </p>
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed">
              {service.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Desafios */}
      <section className="py-16 px-6 md:px-12 bg-brand-surface/30">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-8">
              <Target className="w-8 h-8 text-brand-sage" />
              <h2 className="text-3xl md:text-4xl font-semibold">
                Principais Desafios que Endereçamos
              </h2>
            </div>
            <ul className="space-y-4">
              {service.challenges.map((challenge, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-brand-sage flex-shrink-0 mt-1" />
                  <span className="text-lg text-gray-300">{challenge}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Entregáveis */}
      <section className="py-16 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-8">
              <Zap className="w-8 h-8 text-brand-sage" />
              <h2 className="text-3xl md:text-4xl font-semibold">
                Entregáveis Principais
              </h2>
            </div>
            <ul className="space-y-4">
              {service.deliverables.map((deliverable, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-brand-sage flex-shrink-0 mt-1" />
                  <span className="text-lg text-gray-300">{deliverable}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Service Types - Only for Cloud Pública e Privada */}
      {service.serviceTypes && (
        <section className="py-16 px-6 md:px-12 bg-brand-surface/30">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-semibold mb-4">
                Dois Níveis de Serviço
              </h2>
              <p className="text-lg text-gray-300">
                Escolha o modelo que melhor se adapta às necessidades do seu negócio
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {service.serviceTypes.map((type, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-brand-dark border border-white/10 rounded-xl p-8 hover:border-brand-sage/50 transition-all"
                >
                  <h3 className="text-2xl font-bold mb-2">{type.title}</h3>
                  <p className="text-brand-sage font-semibold mb-4">{type.subtitle}</p>
                  <p className="text-gray-300 mb-6">{type.description}</p>
                  <ul className="space-y-3">
                    {type.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-brand-sage flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-400">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CloudStack Features - Only for Cloud Privada */}
      {service.cloudStackFeatures && (
        <section className="py-16 px-6 md:px-12">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Infraestrutura Completa <span className="text-brand-sage">Apache CloudStack</span>
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Plataforma IaaS open-source de nível empresarial com recursos completos para compute, storage, networking e muito mais
              </p>
            </motion.div>

            {/* Compute */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <Server className="w-10 h-10 text-brand-sage" />
                <h3 className="text-3xl font-bold">Compute</h3>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                {service.cloudStackFeatures.compute.services.map((svc, index) => (
                  <div key={index} className="bg-brand-surface/30 border border-white/10 rounded-lg p-6">
                    <h4 className="text-xl font-bold mb-2">{svc.name}</h4>
                    <p className="text-brand-sage text-sm font-semibold mb-3">{svc.subtitle}</p>
                    <p className="text-gray-400 text-sm mb-4">{svc.description}</p>
                    <ul className="space-y-2">
                      {svc.features.map((feat, fIndex) => (
                        <li key={fIndex} className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 bg-brand-sage rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-sm text-gray-400">{feat}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Storage */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <Database className="w-10 h-10 text-brand-sage" />
                <h3 className="text-3xl font-bold">Storage</h3>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                {service.cloudStackFeatures.storage.services.map((svc, index) => (
                  <div key={index} className="bg-brand-surface/30 border border-white/10 rounded-lg p-6">
                    <h4 className="text-xl font-bold mb-2">{svc.name}</h4>
                    <p className="text-brand-sage text-sm font-semibold mb-3">{svc.subtitle}</p>
                    <p className="text-gray-400 text-sm mb-4">{svc.description}</p>
                    <ul className="space-y-2">
                      {svc.features.map((feat, fIndex) => (
                        <li key={fIndex} className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 bg-brand-sage rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-sm text-gray-400">{feat}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Security */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <Shield className="w-10 h-10 text-brand-sage" />
                <h3 className="text-3xl font-bold">Security</h3>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                {service.cloudStackFeatures.security.services.map((svc, index) => (
                  <div key={index} className="bg-brand-surface/30 border border-white/10 rounded-lg p-6">
                    <h4 className="text-xl font-bold mb-2">{svc.name}</h4>
                    <p className="text-brand-sage text-sm font-semibold mb-3">{svc.subtitle}</p>
                    <p className="text-gray-400 text-sm mb-4">{svc.description}</p>
                    <ul className="space-y-2">
                      {svc.features.map((feat, fIndex) => (
                        <li key={fIndex} className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 bg-brand-sage rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-sm text-gray-400">{feat}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Database */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <Database className="w-10 h-10 text-brand-sage" />
                <h3 className="text-3xl font-bold">Database</h3>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {service.cloudStackFeatures.database.services.map((svc, index) => (
                  <div key={index} className="bg-brand-surface/30 border border-white/10 rounded-lg p-6">
                    <h4 className="text-xl font-bold mb-2">{svc.name}</h4>
                    <p className="text-brand-sage text-sm font-semibold mb-3">{svc.subtitle}</p>
                    <p className="text-gray-400 text-sm mb-4">{svc.description}</p>
                    <ul className="space-y-2">
                      {svc.features.map((feat, fIndex) => (
                        <li key={fIndex} className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 bg-brand-sage rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-sm text-gray-400">{feat}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Network */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <Cloud className="w-10 h-10 text-brand-sage" />
                <h3 className="text-3xl font-bold">Network</h3>
              </div>
              <div className="grid md:grid-cols-1 gap-6">
                {service.cloudStackFeatures.network.services.map((svc, index) => (
                  <div key={index} className="bg-brand-surface/30 border border-white/10 rounded-lg p-6">
                    <h4 className="text-xl font-bold mb-2">{svc.name}</h4>
                    <p className="text-brand-sage text-sm font-semibold mb-3">{svc.subtitle}</p>
                    <p className="text-gray-400 text-sm mb-4">{svc.description}</p>
                    <ul className="grid md:grid-cols-2 gap-3">
                      {svc.features.map((feat, fIndex) => (
                        <li key={fIndex} className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 bg-brand-sage rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-sm text-gray-400">{feat}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Technical Highlights - Only for Cloud Pública e Privada */}
      {service.technicalHighlights && (
        <section className="py-16 px-6 md:px-12 bg-brand-surface/30">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-semibold mb-4">
                Características Técnicas Principais
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {service.technicalHighlights.map((highlight, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="bg-brand-dark border border-white/10 rounded-lg p-6"
                >
                  <h4 className="text-lg font-bold mb-2 text-brand-sage">{highlight.title}</h4>
                  <p className="text-sm text-gray-400">{highlight.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Quando faz sentido */}
      <section className="py-16 px-6 md:px-12 bg-brand-surface/30">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-semibold mb-8">
              Quando Este Serviço Faz Sentido
            </h2>
            <ul className="space-y-4">
              {service.whenMakesSense.map((scenario, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-brand-sage flex-shrink-0 mt-1" />
                  <span className="text-lg text-gray-300">{scenario}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Resultados */}
      <section className="py-16 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-8">
              <TrendingUp className="w-8 h-8 text-brand-sage" />
              <h2 className="text-3xl md:text-4xl font-semibold">
                Resultados Esperados
              </h2>
            </div>
            <ul className="space-y-4">
              {service.results.map((result, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-brand-sage flex-shrink-0 mt-1" />
                  <span className="text-lg text-gray-300">{result}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      {service.faq && (
        <section className="py-16 px-6 md:px-12 bg-brand-surface/30">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-semibold mb-8">
                Perguntas Frequentes
              </h2>
              <div className="space-y-6">
                {service.faq.map((item) => (
                  <div key={item.question} className="border border-white/10 bg-brand-dark/60 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-brand-sage mb-3">{item.question}</h3>
                    <p className="text-gray-300 leading-relaxed">{item.answer}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-brand-surface/30">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">
              Pronto para Começar?
            </h2>
            <p className="text-lg text-gray-300 mb-8">
              Fale com nossos especialistas e descubra como podemos ajudar seu negócio.
            </p>
            <a
              href="/#contato"
              className="inline-block bg-brand-sage text-brand-dark px-8 py-4 rounded-md font-semibold hover:bg-opacity-90 transition-all text-center"
              data-testid="service-cta-button"
              aria-label="Agendar Consulta"
            >
              Agendar Consulta
            </a>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <img src={LOGO_URL} alt="Sua Nuvem" className="h-12 w-auto mb-4" width="120" height="48" />
              <p className="text-gray-400 text-sm">
                Consultoria especializada em DevOps e infraestrutura em nuvem.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Links Rápidos</h4>
              <div className="space-y-2">
                <Link to="/" className="block text-gray-400 hover:text-brand-sage transition-colors text-sm">
                  Início
                </Link>
                <Link to="/#solucoes" className="block text-gray-400 hover:text-brand-sage transition-colors text-sm">
                  Soluções
                </Link>
                <Link to="/cases" className="block text-gray-400 hover:text-brand-sage transition-colors text-sm">
                  Cases
                </Link>
                <Link to="/#contato" className="block text-gray-400 hover:text-brand-sage transition-colors text-sm">
                  Contato
                </Link>
                <Link to="/blog" className="block text-gray-400 hover:text-brand-sage transition-colors text-sm">
                  Blog
                </Link>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Contato</h4>
              <div className="space-y-2 text-sm text-gray-400">
                <p>suanuvem.com</p>
                <p>@suanuvem.io</p>
              </div>
            </div>
          </div>
          
          <div className="border-t border-white/10 pt-8 text-center text-sm text-gray-400">
            <p>© 2026 Sua Nuvem. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default ServicePage;
